import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Trophy, Check, ChevronRight, Tag } from 'lucide-react';

const plans = [
  {
    name: 'Free',
    priceMonthly: 0,
    priceAnnual: 0,
    promoPrice: null,
    description: 'Perfect for trying out',
    features: ['1 tournament', '4 teams max', 'Basic features', 'Community support'],
    cta: 'Start Free',
    popular: false
  },
  {
    name: 'Starter',
    priceMonthly: 15,
    priceAnnual: 162,
    promoPrice: 9.99,
    description: 'For small leagues',
    features: ['3 tournaments', '8 teams per tournament', 'No ads', 'Email support', 'Custom colors'],
    cta: 'Get Started',
    popular: false
  },
  {
    name: 'Pro',
    priceMonthly: 39,
    priceAnnual: 421,
    promoPrice: 29.99,
    description: 'For serious organizers',
    features: ['Unlimited tournaments', '16 teams per tournament', 'Custom branding', 'Priority support', 'Sponsor management (5)'],
    cta: 'Go Pro',
    popular: true
  },
  {
    name: 'Enterprise',
    priceMonthly: 99,
    priceAnnual: 1069,
    promoPrice: 79.99,
    description: 'For organizations',
    features: ['Everything in Pro', 'White-label solution', 'API access', 'Dedicated support', 'Unlimited sponsors'],
    cta: 'Contact Sales',
    popular: false
  }
];

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  const getPrice = (plan) => {
    return billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
  };
  
  const getMonthlyEquivalent = (plan) => {
    if (billingCycle === 'annual' && plan.priceAnnual > 0) {
      return (plan.priceAnnual / 12).toFixed(0);
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-12 w-auto object-contain"
              />
              <span className="font-heading text-2xl font-bold text-white uppercase tracking-wide">
                Score Copilot
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                Contact
              </Link>
              <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white uppercase mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Choose the plan that fits your tournament size. Start free and upgrade as you grow.
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex flex-col items-center gap-4 mb-10">
            <div className="flex items-center bg-[#18181b] border border-white/10 rounded-lg p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                data-testid="pricing-monthly-toggle"
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-emerald-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                data-testid="pricing-annual-toggle"
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'annual'
                    ? 'bg-emerald-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Annual
                <span className="px-2 py-0.5 bg-yellow-500 text-black text-xs rounded-full font-bold">
                  Save 10%
                </span>
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Tag className="text-emerald-500" size={16} />
              <span className="text-gray-400">Use code</span>
              <span className="text-emerald-500 font-mono font-bold">SCORE5</span>
              <span className="text-gray-400">for 5% off (valid until April 2026)</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => {
              const price = getPrice(plan);
              const monthlyEquiv = getMonthlyEquivalent(plan);
              const hasPromo = plan.promoPrice && billingCycle === 'monthly';
              
              return (
                <div 
                  key={index}
                  className={`relative bg-[#18181b] border rounded-xl p-6 ${
                    plan.popular 
                      ? 'border-emerald-500 emerald-glow' 
                      : 'border-white/10'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 rounded-full text-xs font-medium text-white">
                      Most Popular
                    </div>
                  )}
                  {hasPromo && (
                    <div className="absolute -top-3 right-4 px-3 py-1 bg-orange-500 rounded-full text-xs font-bold text-white animate-pulse">
                      Limited Time
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="font-heading text-2xl font-bold text-white uppercase">{plan.name}</h3>
                    <p className="text-gray-500 text-sm mt-1">{plan.description}</p>
                  </div>
                  <div className="mb-6">
                    {hasPromo ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg text-gray-500 line-through">${plan.priceMonthly}</span>
                          <span className="text-4xl font-bold text-emerald-400">${plan.promoPrice}</span>
                          <span className="text-gray-500">/month</span>
                        </div>
                        <p className="text-orange-400 text-xs mt-1 font-medium">
                          Lock in this price forever when you sign up now
                        </p>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-white">${price}</span>
                        <span className="text-gray-500">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                        {monthlyEquiv && (
                          <p className="text-emerald-500 text-sm mt-1">
                            ${monthlyEquiv}/mo equivalent
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-gray-300">
                        <Check className="text-emerald-500 flex-shrink-0" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button 
                      className={`w-full ${
                        plan.popular 
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                          : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                      }`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>

          {/* FAQ Link */}
          <div className="text-center mt-16">
            <p className="text-gray-400">
              Have questions? Check our{' '}
              <a href="#" className="text-emerald-500 hover:text-emerald-400">FAQ</a>
              {' '}or{' '}
              <a href="#" className="text-emerald-500 hover:text-emerald-400">contact support</a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0f0f10] to-[#0a0a0b]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Create your first tournament in minutes. No credit card required.
          </p>
          <Link to="/register">
            <Button 
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg btn-lift"
            >
              Start Free Tournament
              <ChevronRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-8">
            <img src="/cnvt-logo.jpg" alt="CNVT" className="w-10 h-10 rounded object-contain" />
            <span className="text-gray-600 text-sm">|</span>
            <img src="/kilimanjaro-logo.png" alt="Kilimanjaro" className="h-10 object-contain" />
            <span className="text-gray-600 text-sm">|</span>
            <img src="/africremit-logo.jpg" alt="Afric Remit" className="h-10 object-contain rounded" />
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-10 w-auto object-contain"
              />
              <span className="font-heading text-xl font-bold text-white uppercase">Score Copilot</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 Sports Copilot. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
